import React, { useEffect, useMemo, useRef, useState } from "react";
import { DateBox } from "devextreme-react/date-box";
import PivotGrid, { FieldChooser, FieldPanel } from "devextreme-react/pivot-grid";
import PivotGridDataSource from "devextreme/ui/pivot_grid/data_source";
import "./PositionsPositions.css";
import { HeaderFilter } from "devextreme-react/data-grid";
import { Search } from "devextreme-react/gantt";
import { CheckBox } from "devextreme-react";
import { Workbook } from "exceljs";

import "jspdf-autotable";
import jsPDF from "jspdf";
import saveAs from "file-saver";
import * as ExcelJS from "exceljs";
import { exportPivotGrid as exportPivotGridXlsx } from "devextreme/excel_exporter";
import { baseUrl } from "../../hooks/useFetch";
import { toDate, fmt, fmtUK } from "../../utils/dateUtils";
import useCobDate from "../../hooks/useCobDate";

interface PivotField {
  caption?: string;
  dataField?: string;
  name?: string;
  area?: string;
  filterValues?: unknown[];
  filterType?: string;
  searchValue?: string;
}

interface FlatLeaf {
  label: string;
  index: number;
  pos: number;
}

function formatFilters(fields: PivotField[]): string[] {
  const items = (fields || [])
    .filter(
      (f) =>
        f &&
        f.area !== "data" &&
        ((Array.isArray(f.filterValues) && f.filterValues.length > 0) ||
          (typeof f.searchValue === "string" && f.searchValue.length > 0))
    )
    .map((f) => {
      const cap = f.caption || f.dataField || f.name || "Field";
      const op = (f.filterType || "include") === "exclude" ? "≠" : "=";
      const vals = (f.filterValues || []).map((v) =>
        Array.isArray(v) ? v.join(" / ") : String(v)
      );
      return f.searchValue
        ? `${cap} ${op} ${vals.join(", ")} (search: "${f.searchValue}")`
        : `${cap} ${op} ${vals.join(", ")}`;
    });

  return items; // array of strings
}

interface PositionsSPPivotTableProps {
  page?: string;
  cobDate?: Date | null;
  compareDate?: Date | null;
}

const PositionsSPPivotTable = React.forwardRef<any, PositionsSPPivotTableProps>((_props, _ref) => {
  const pivotRef = useRef<any>(null);
  const [cobDate, setCobDate, cobLoading] = useCobDate("/positions_latest_cob");
  const [compareDate, setCompareDate] = useState<Date | null>(null);
  const [positionData, setPositionData] = useState<any[]>([]);
  const [compareData, setCompareData] = useState<any[]>([]);

  const [mode, setMode] = useState<string>("current");
  const [searchEnabled, setSearchEnabled] = useState<boolean>(true);
  const [showRelevantValues, setShowRelevantValues] = useState<boolean>(true);

  const prevCob = useRef<Date | null>(null);
  const prevCmp = useRef<Date | null>(null);

  useEffect(() => {
    if (!prevCob.current || (cobDate && cobDate.getTime() !== prevCob.current.getTime())) {
      setMode("current");
      prevCob.current = cobDate;
    } else if (
      compareDate &&
      (!prevCmp.current || compareDate.getTime() !== prevCmp.current.getTime())
    ) {
      setMode("diff");
      prevCmp.current = compareDate;
    }
  }, [cobDate, compareDate]);

  useEffect(() => {
    if (cobDate == null) return;

    const ac = new AbortController();
    const prev = document.body.style.cursor;
    document.body.style.cursor = "wait";

    (async () => {
      try {
        setCompareDate(null);
        const r = await fetch(
          `${baseUrl}/positions_sp_exposure_trades?cob_date=${fmt(cobDate)}`,
          { signal: ac.signal }
        );
        const { data } = await r.json();
        setPositionData(data || []);
      } catch (e: any) {
        if (e?.name !== "AbortError") console.error(e);
      } finally {
        document.body.style.cursor = "";
      }
    })();

    return () => {
      ac.abort();
      document.body.style.cursor = "";
    };
  }, [cobDate, baseUrl]);

  const didMount = useRef<boolean>(false);

  const activeStore = mode === "current" ? positionData : compareData;

  const label =
    mode === "current"
      ? `Showing: COB ${fmtUK(cobDate)}`
      : `Showing: Δ ${fmtUK(cobDate)} → ${fmtUK(compareDate)}`;

  const metricLabels: Record<string, string> = {
    mtm: "MTM",
    delta: "Delta",
    gamma: "Gamma",
    vega: "Vega",
    theta: "Theta",
    rho: "Rho",
  };

  const fields = useMemo(
    () => [
      { caption: "CoB Date", dataField: "cob_date" },
      { caption: "Trade ID", dataField: "trade_id" },

      { caption: "Client", dataField: "client", area: "filter" },
      { caption: "OTC Trade Type", dataField: "otc_trade_type" },
      { caption: "Contract", dataField: "contract" },
      { caption: "Expiry", dataField: "expiry" },
      { caption: "Commodity", dataField: "commodity", area: "filter" },

      { caption: "AssetType", dataField: "assettype", area: "filter" },
      { caption: "AssetCategory", dataField: "assetcategory", area: "column" },
      { caption: "AssetClass", dataField: "assetclass", area: "column" },
      { caption: "AssetSubClass", dataField: "assetclass1", area: "column" },
      { caption: "Tenor", dataField: "tenor", area: "column" },
      { caption: "Underlying", dataField: "underlying", area: "column" },

      { caption: "Strike LL", dataField: "strike_ll", area: "filter" },
      { caption: "Barrier UL", dataField: "barrier_ul", area: "filter" },
      { caption: "LL Distance", dataField: "ll_distance", area: "filter" },
      { caption: "UL Distance", dataField: "ul_distance", area: "filter" },

      {
        caption: metricLabels.mtm,
        dataField: "mtm",
        summaryType: "sum",
        format: "fixedPoint",
      },
      {
        caption: metricLabels.delta,
        dataField: "delta",
        area: "data",
        summaryType: "sum",
        format: "fixedPoint",
      },
      {
        caption: metricLabels.gamma,
        dataField: "gamma",
        area: "data",
        summaryType: "sum",
        format: "fixedPoint",
      },
      {
        caption: metricLabels.vega,
        dataField: "vega",
        area: "data",
        summaryType: "sum",
        format: "fixedPoint",
      },
      {
        caption: metricLabels.theta,
        dataField: "theta",
        area: "data",
        summaryType: "sum",
        format: "fixedPoint",
      },
      {
        caption: metricLabels.rho,
        dataField: "rho",
        area: "data",
        summaryType: "sum",
        format: "fixedPoint",
      },
    ],
    []
  );

  const dataSource = useMemo(
    () => {
      const mappedFields: any[] = fields.map((f) => {
        const isMetric = ["mtm", "delta", "gamma", "vega", "theta", "rho"].includes(
          f.dataField
        );

        if (isMetric) {
          return {
            ...f,
            dataType: "number" as const,
            format: { type: "fixedPoint", precision: 0 },
            customizeText: (cellInfo: any) => {
              const v = cellInfo?.value;
              if (!Number.isFinite(v))
                return cellInfo.text ?? cellInfo.valueText ?? "";
              const abs = Math.abs(v).toLocaleString(undefined, {
                maximumFractionDigits: 0,
              });
              return v < 0 ? `(${abs})` : abs;
            },
          };
        }

        if (f.dataField === "assettype") {
          return {
            ...f,
            area: "filter",
            filterType: "include" as const,
          };
        }

        if (f.dataField === "cob_date") {
          return {
            ...f,
            dataType: "date" as const,
          };
        }

        return f;
      });
      return new PivotGridDataSource({
        fields: mappedFields,
        store: activeStore || [],
      });
    },
    [fields, activeStore, mode]
  );

  const onExport = async (): Promise<void> => {
    if (!pivotRef.current) return;

    const pivotInstance = pivotRef.current.instance();

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Pivot");

    await exportPivotGridXlsx({
      component: pivotInstance,
      worksheet,
      customizeCell: ({ pivotCell, excelCell }: any) => {
        if (!pivotCell) return;
        if (
          pivotCell.area === "row" ||
          pivotCell.area === "column" ||
          pivotCell.area === "filter" ||
          (pivotCell.area === "data" &&
            pivotCell.rowType === "D" &&
            pivotCell.columnType === "D" &&
            pivotCell.type === "D")
        ) {
          excelCell.font = {
            ...(excelCell.font || {}),
            bold: true,
          };
        }
        if (pivotCell.area === "data" && pivotCell.value != null) {
          const numericValue = Number(pivotCell.value);

          if (!Number.isNaN(numericValue)) {
            excelCell.value = numericValue;
            excelCell.numFmt = "#,##0.00;[Red]-#,##0.00";

            if (numericValue < 0) {
              excelCell.font = {
                ...(excelCell.font || {}),
                color: { argb: "FFFF0000" },
              };
            }
          }
        }
      },
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "positions.xlsx");
  };

  const flattenLeavesWithPos = (items: any[] = []): FlatLeaf[] => {
    const out: FlatLeaf[] = [];
    let pos = 0;

    const walk = (nodes: any[], path: string[] = []): void => {
      for (const n of nodes) {
        const nodeLabel = n.text ?? n.value ?? "";
        const nextPath = [...path, nodeLabel];

        if (n.children && n.children.length) walk(n.children, nextPath);
        else out.push({ label: nextPath.join(" / "), index: n.index, pos: pos++ });
      }
    };

    walk(items);
    return out;
  };

  const unwrapCell = (cellArr: any, idx: number = 0): any => {
    const raw = Array.isArray(cellArr) ? cellArr[idx] : cellArr;
    if (raw == null) return "";
    if (typeof raw === "object" && raw !== null && "value" in raw) return raw.value;
    return raw;
  };

  const onExportPdf = async (): Promise<void> => {
    const pivot = pivotRef.current.instance();
    const ds = pivot.getDataSource();

    await ds.reload();

    const data = ds.getData();
    const { rows = [], columns = [], values = [] } = data;

    const dataFields = ds.getAreaFields("data") || [];
    const flatCols = flattenLeavesWithPos(columns);

    const d = new Date().toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    const round2 = (v: any): number | any => {
      const n = Number(v);
      return Number.isFinite(n) ? Math.round(n * 100) / 100 : v;
    };

    const head = [["Row", ...(flatCols.length ? flatCols.map((c) => c.label) : ["Value"])]];

    const body: any[][] = [];

    if (rows.length === 0 && values?.length === 1 && dataFields.length > 0) {
      for (let m = 0; m < dataFields.length; m++) {
        const measureName =
          dataFields[m].caption || dataFields[m].dataField || `Measure ${m + 1}`;

        const rowVals = (flatCols.length ? flatCols : [{ index: 0, pos: 0 }]).map(
          (c: any, colPos: number) => {
            const cKey = Number.isInteger(c.index) ? c.index : colPos;
            const v = unwrapCell(values[0]?.[cKey], m);
            return round2(v);
          }
        );

        body.push([measureName, ...rowVals]);
      }
    } else {
      const flatRows = flattenLeavesWithPos(rows);

      for (const r of flatRows) {
        const rKey = Number.isInteger(r.index) ? r.index : r.pos;

        const rowVals = (flatCols.length ? flatCols : [{ index: 0, pos: 0 }]).map(
          (c: any, colPos: number) => {
            const cKey = Number.isInteger(c.index) ? c.index : colPos;
            const v = unwrapCell(values?.[rKey]?.[cKey], 0);
            const num = Number(v);
            return Number.isNaN(num) ? v : num;
          }
        );

        body.push([r.label, ...rowVals]);
      }
    }

    const doc = new jsPDF("landscape", "pt", "a4");
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(`Positions ${d}`, 20, 25);

    (doc as any).autoTable({
      head,
      body,
      styles: { fontSize: 9 },
      headStyles: {
        fontStyle: "bold",
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
      },
      didParseCell: ({ section, cell }: any) => {
        if (
          section === "body" &&
          typeof cell.raw === "number" &&
          cell.raw < 0
        ) {
          cell.styles.textColor = [255, 0, 0];
          cell.styles.fontStyle = "bold";
        }
      },
      margin: { top: 40, left: 20, right: 20, bottom: 20 },
    });

    doc.save("pivot.pdf");
  };

  const onCellPrepared = (e: any): void => {
    const v = e.cell?.value;
    e.cellElement.classList.toggle("neg", Number.isFinite(v) && v < 0);
  };

  const [filterChips, setFilterChips] = useState<string[]>([]);
  const lastSig = useRef<string>("");

  useEffect(() => {
    const ds = dataSource;
    if (!ds) return;

    const extract = () => {
      const chips = formatFilters(ds.fields() as PivotField[]);
      const sig = JSON.stringify(chips);
      if (sig !== lastSig.current) {
        lastSig.current = sig;
        setFilterChips(chips);
      }
    };

    extract();
    ds.on("changed", extract);

    return () => {
      try {
        ds.off("changed", extract);
      } catch {}
    };
  }, [dataSource]);

  return (
    <div className="dashboard-container">
      <div className="filter-summary">
        {filterChips.length ? (
          filterChips.map((txt, i) => (
            <span key={i} className="chip">
              {txt}
            </span>
          ))
        ) : (
          <em>No filters applied</em>
        )}
      </div>

      <div className="positions-pivot-section">
        <div className="positions-pivot-scroll">
          <PivotGrid
            ref={pivotRef}
            id="pivotgrid"
            dataSource={dataSource}
            allowFiltering={true}
            allowSorting={true}
            allowSortingBySummary={true}
            showBorders={true}
            // @ts-ignore
            showDataFields
            dataFieldArea="row"
            showRowFields
            showColumnFields
            height="70vh"
            width="100%"
            onCellPrepared={onCellPrepared}
          >
            <HeaderFilter
              {...{ showRelevantValues } as any}
              width={300}
              height={400}
            >
              <Search enabled={searchEnabled} />
            </HeaderFilter>
            <FieldChooser allowSearch={true} />
            <FieldPanel visible={true} />
          </PivotGrid>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "12px",
          marginTop: "20px",
        }}
      >
        <button
          onClick={onExport}
          style={{
            padding: "10px 18px",
            borderRadius: "4px",
            backgroundColor: "#28a745",
            color: "white",
            border: "none",
            cursor: "pointer",
          }}
        >
          Export to Excel
        </button>
        <button
          onClick={onExportPdf}
          style={{
            padding: "10px 18px",
            borderRadius: "4px",
            backgroundColor: "#d9534f",
            color: "white",
            border: "none",
            cursor: "pointer",
          }}
        >
          Export to PDF
        </button>
      </div>
    </div>
  );
});

export default PositionsSPPivotTable;
